import { useState, type ReactNode } from 'react';
import { type ChatMessage, DEFAULT_USER_COLOR } from '../../lib/twitch.js';
import { useAuth } from '../../context/AuthContext.js';
import { useModActions } from '../../hooks/useModActions.js';
import { tokenize, type SevenTVEmote } from '../../lib/emotes.js';
import type { BadgeSetMap } from '../../hooks/useBadges.js';

interface Props {
  msg: ChatMessage;
  deleted?: boolean;
  /** Open the user-info modal (mod-only). Receives the **Twitch** user id. */
  onOpenUserInfo?: (twitchUserId: string) => void;
  emotes?: Map<string, SevenTVEmote>;
  badgeMap?: BadgeSetMap;
  broadcasterAvatar?: string | null;
  /** Maps "username\0text" → rewardTitle for channel point redemptions. */
  redemptionMap?: Map<string, string>;
  /** Twitch channel profile image URL (for StreamTogether shared chat). */
  channelAvatarUrl?: string;
  /** Called when the user clicks "Antworten" on this message. */
  onReply?: (msg: ChatMessage) => void;
}

const URL_REGEX = /https?:\/\/[^\s<>"']+/g;

/** Split a plain-text string into text and clickable URL nodes. */
function splitByUrls(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(text)) !== null) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const url = match[0];
    nodes.push(
      <a
        key={`${keyPrefix}-${match.index}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-400 hover:text-blue-300 underline break-all"
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>,
    );
    last = match.index + url.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/** Render a plain text segment with 7TV emote substitution and URL linking. */
function renderSegment(text: string, sevenTV: Map<string, SevenTVEmote>): ReactNode[] {
  return tokenize(text, sevenTV).flatMap((token, i) => {
    if (token.kind !== 'text') {
      return [
        <img
          key={`7tv-${token.emote.id}-${i}`}
          src={token.emote.url1x}
          srcSet={`${token.emote.url1x} 1x, ${token.emote.url2x} 2x`}
          alt={token.emote.name}
          title={token.emote.name}
          className="inline-block align-middle h-5 mx-0.5"
          loading="lazy"
        />,
      ];
    }
    return splitByUrls(token.text, `url-${i}`);
  });
}

/**
 * Render chat message text with Twitch emotes (position-based) and
 * 7TV emotes (word-matching) applied in the remaining text segments.
 */
function renderText(msg: ChatMessage, sevenTV: Map<string, SevenTVEmote>): ReactNode[] {
  const text = msg.text;
  const ranges: Array<{ start: number; end: number; id: string }> = [];
  for (const [id, positions] of Object.entries(msg.emotes)) {
    for (const pos of positions) {
      const [s, e] = pos.split('-').map((n) => Number(n));
      if (Number.isFinite(s) && Number.isFinite(e)) {
        ranges.push({ start: s as number, end: e as number, id });
      }
    }
  }
  ranges.sort((a, b) => a.start - b.start);

  const out: ReactNode[] = [];
  let cursor = 0;
  for (const r of ranges) {
    if (r.start > cursor) {
      out.push(...renderSegment(text.slice(cursor, r.start), sevenTV));
    }
    const code = text.slice(r.start, r.end + 1);
    out.push(
      <img
        key={`twitch-${r.id}-${r.start}`}
        src={`https://static-cdn.jtvnw.net/emoticons/v2/${r.id}/default/dark/1.0`}
        alt={code}
        className="inline-block align-middle h-5 mx-0.5"
        loading="lazy"
      />,
    );
    cursor = r.end + 1;
  }
  if (cursor < text.length) out.push(...renderSegment(text.slice(cursor), sevenTV));
  return out;
}

/** Badge sets shown in chat (all others hidden). */
const VISIBLE_BADGES = new Set(['vip', 'moderator', 'lead_moderator', 'subscriber', 'broadcaster', 'member', 'verified']);

/** Subscriber version thresholds for tier detection. */
function subTier(version: string): 1 | 2 | 3 {
  const v = Number(version);
  if (v >= 2000) return 3;
  if (v >= 1000) return 2;
  return 1;
}

function BadgeImg({ src, alt }: { src: string; alt: string }) {
  return (
    <img
      src={src}
      alt={alt}
      title={alt}
      className="inline-block w-4 h-4 align-middle"
    />
  );
}

function renderBadge(
  badge: { id: string; version: string },
  badgeMap: BadgeSetMap,
): ReactNode {
  // Lead Moderator: Twitch serves the image under the same set_id as the
  // IRC/EventSub badges tag (`lead_moderator`, underscore), so the normal
  // lookup resolves the original badge — no special-casing needed.
  const versions = badgeMap.get(badge.id);
  if (versions) {
    // For subscriber: version encodes tier + months (T1: 0-999, T2: 1000-1999, T3: 2000+).
    // Pick the highest available milestone badge that is <= the user's actual version.
    if (badge.id === 'subscriber') {
      const tier = subTier(badge.version);
      const tierMin = tier === 3 ? 2000 : tier === 2 ? 1000 : 0;
      const tierMax = tier === 3 ? Infinity : tier === 2 ? 1999 : 999;
      const v = Number(badge.version);
      // Best match: highest uploaded milestone <= user's version within same tier
      const best = Array.from(versions.keys())
        .map(Number)
        .filter((k) => k >= tierMin && k <= Math.min(tierMax, v))
        .sort((a, b) => b - a)[0];
      const url = (best !== undefined ? versions.get(String(best)) : undefined)
               ?? versions.get(String(tierMin))
               ?? versions.values().next().value;
      if (url) return <BadgeImg key={`${badge.id}-${badge.version}`} src={url} alt={`Sub T${tier}`} />;
    } else {
      const url = versions.get(badge.version) ?? versions.get('1') ?? versions.values().next().value;
      if (url) return <BadgeImg key={`${badge.id}-${badge.version}`} src={url} alt={badge.id} />;
    }
  }

  // Fallback text symbols when badge API returned no image
  const fallback: Record<string, string> = {
    moderator: '⚔',
    lead_moderator: '⚔',
    vip: '◆',
    subscriber: '♥',
    broadcaster: '👑',
    member: '⭐',
    verified: '✓',
  };
  const sym = fallback[badge.id];
  if (sym) {
    return (
      <span key={badge.id} title={badge.id} className="inline-block w-4 text-center text-xs">
        {sym}
      </span>
    );
  }
  return null;
}

function TwitchIcon() {
  return (
    <svg viewBox="0 0 24 28" className="inline-block w-3.5 h-3.5" fill="currentColor" aria-label="Twitch">
      <path d="M2.149 0L0 6.362v19.64h6.362V30h3.63l3.857-3.998h5.449L24 20.555V0H2.149zm19.851 19.447l-4.218 4.218H12l-3.858 3.999v-4h-5V2h19v17.447z" />
      <path d="M11 7h2v6h-2zM16 7h2v6h-2z" />
    </svg>
  );
}

function YouTubeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="inline-block w-3.5 h-3.5" fill="currentColor" aria-label="YouTube">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}

export default function ChatMessage({ msg, deleted = false, onOpenUserInfo, emotes = new Map(), badgeMap = new Map(), broadcasterAvatar, redemptionMap, channelAvatarUrl, onReply }: Props) {
  const color = msg.color || DEFAULT_USER_COLOR;
  const { isMod } = useAuth();
  const { deleteMessage, timeoutUser, banUser } = useModActions();
  const [actionBusy, setActionBusy] = useState(false);

  const isYouTube = msg.source === 'youtube';
  const rewardTitle = !isYouTube && redemptionMap
    ? redemptionMap.get(`${msg.username}\0${msg.text}`) ?? null
    : null;
  const isSuperChat = isYouTube && msg.type === 'superChat';
  const isNewSponsor = isYouTube && msg.type === 'newSponsor';
  const isHighlighted = !isYouTube && (msg as { isHighlighted?: boolean }).isHighlighted === true;

  async function runAction(label: string, fn: () => Promise<void>): Promise<void> {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      await fn();
    } catch (err) {
      console.warn(`Mod-Action ${label} fehlgeschlagen`, err);
    } finally {
      setActionBusy(false);
    }
  }

  // Message background: SuperChat > Highlight (Meine Nachricht hervorheben) > default
  const containerExtra = isSuperChat
    ? 'bg-yellow-500/10 border-l-2 border-yellow-400'
    : isHighlighted
    ? 'bg-purple-900/20 border-l-2 border-purple-500'
    : 'border-l-2 border-transparent';

  const replyMeta = msg.source === 'twitch' ? (msg as { replyParentMeta?: { userLogin: string; displayName: string; msgBody: string } | null }).replyParentMeta : null;

  return (
    <div
      className={`group relative px-3 py-1 text-sm leading-snug break-words ${
        deleted ? 'opacity-40 italic' : ''
      } ${isMod || onReply ? 'hover:bg-slate-900/40' : ''} ${containerExtra}`}
    >
      {/* Reply-to context — shown when this message is a reply */}
      {replyMeta && (
        <div className="flex items-center gap-1 mb-0.5 text-xs text-slate-500 truncate">
          <svg className="w-3 h-3 flex-shrink-0 text-slate-600" viewBox="0 0 16 16" fill="currentColor">
            <path d="M6 4.5L1 9l5 4.5V11h4c2.2 0 4 1.8 4 4v.5c.7-1 1-2.2 1-3.5 0-3.3-2.7-6-6-6H6V4.5z"/>
          </svg>
          <span className="text-slate-400 font-semibold">@{replyMeta.displayName}</span>
          <span className="truncate">{replyMeta.msgBody}</span>
        </div>
      )}
      {/* Platform source icon / channel avatar */}
      {isYouTube ? (
        <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm bg-red-600 text-white mr-1 align-middle">
          <YouTubeIcon />
        </span>
      ) : channelAvatarUrl ? (
        <img
          src={channelAvatarUrl}
          alt={msg.source === 'twitch' ? (msg as { channel?: string }).channel ?? 'Twitch' : 'Twitch'}
          title={msg.source === 'twitch' ? (msg as { channel?: string }).channel ?? '' : ''}
          className="inline-block w-4 h-4 rounded-full object-cover mr-1 align-middle"
          loading="lazy"
        />
      ) : (
        <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm bg-purple-600 text-white mr-1 align-middle">
          <TwitchIcon />
        </span>
      )}

      {/* Channel Point reward badge */}
      {rewardTitle && (
        <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-xs font-semibold bg-cyan-900/60 text-cyan-300 mr-1">
          <span>⚡</span>
          <span className="max-w-[120px] truncate">{rewardTitle}</span>
        </span>
      )}

      {/* newSponsor badge */}
      {isNewSponsor && (
        <span className="inline-flex items-center px-1 py-0.5 rounded text-xs font-bold bg-green-600 text-white mr-1">
          NEW
        </span>
      )}

      {/* Broadcaster avatar — shown instead of a badge when source is Twitch broadcaster.
          Hidden when channelAvatarUrl is already set (same image would appear twice). */}
      {!isYouTube && msg.source === 'twitch' &&
        !channelAvatarUrl &&
        msg.badges.some((b) => b.id === 'broadcaster') &&
        broadcasterAvatar && (
          <img
            src={broadcasterAvatar}
            alt="Broadcaster"
            title="Broadcaster"
            className="inline-block w-4 h-4 rounded-full align-middle mr-0.5"
            loading="lazy"
          />
        )
      }

      {/* Visible badges: VIP, Mod, Lead Moderator, Sub T1/T2/T3 */}
      {((): ReactNode => {
        const visibleBadges = msg.badges.filter((b) => VISIBLE_BADGES.has(b.id));
        if (visibleBadges.length === 0) return null;
        return (
          <span className="inline-flex items-center gap-0.5 mr-1 align-middle">
            {visibleBadges.map((b) => renderBadge(b, badgeMap))}
          </span>
        );
      })()}
      <span
        className={`font-semibold ${isMod && !isYouTube ? 'cursor-pointer hover:underline' : ''}`}
        style={{ color }}
        onClick={() => isMod && !isYouTube && onOpenUserInfo?.(msg.userId)}
      >
        {msg.displayName}
      </span>
      <span className="text-slate-500">: </span>
      <span className={msg.isAction ? 'italic' : ''} style={msg.isAction ? { color } : undefined}>
        {deleted ? <em className="text-slate-500">[gelöscht]</em> : renderText(msg, emotes)}
      </span>

      {/* SuperChat: show amount */}
      {isSuperChat && msg.superChatAmount != null && msg.superChatCurrency != null && (
        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-yellow-500 text-black">
          {(msg.superChatAmount / 100).toFixed(2)} {msg.superChatCurrency}
        </span>
      )}

      {onReply && !deleted && (
        <span className="ml-1 hidden group-hover:inline-flex gap-1 align-middle">
          <button
            type="button"
            title="Antworten"
            onClick={() => onReply(msg)}
            className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 hover:bg-purple-900/60 text-purple-400"
          >
            ↩
          </button>
        </span>
      )}
      {isMod && !deleted && (
        <span className="ml-2 hidden group-hover:inline-flex gap-1 align-middle">
          <button
            type="button"
            disabled={actionBusy}
            title="Nachricht löschen"
            onClick={() =>
              void runAction('delete', () => deleteMessage(msg.externalId, msg.source as 'twitch' | 'youtube'))
            }
            className="text-[10px] px-1 py-0.5 rounded bg-slate-800 hover:bg-red-900/60 disabled:opacity-50"
          >
            🗑
          </button>
          <button
            type="button"
            disabled={actionBusy}
            title="Timeout 10 Min"
            onClick={() =>
              void runAction('timeout', () => timeoutUser(msg.userId, 600, undefined, msg.source as 'twitch' | 'youtube'))
            }
            className="text-[10px] px-1 py-0.5 rounded bg-slate-800 hover:bg-yellow-900/60 disabled:opacity-50"
          >
            ⏱10m
          </button>
          <button
            type="button"
            disabled={actionBusy}
            title="Ban"
            onClick={() => void runAction('ban', () => banUser(msg.userId, undefined, msg.source as 'twitch' | 'youtube'))}
            className="text-[10px] px-1 py-0.5 rounded bg-slate-800 hover:bg-red-900/80 disabled:opacity-50"
          >
            ⛔
          </button>
        </span>
      )}
    </div>
  );
}
